import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, MessageCircle } from "lucide-react";
import { API, adminHeaders } from "@/shop/api";
import { inr } from "@/shop/products";
import { RequireAdmin, AdminShell } from "@/pages/AdminShell";

const STATUS_STYLE = {
  paid: "border-[#7ED321]/40 bg-[#7ED321]/10 text-[#7ED321]",
  cod_pending: "border-yellow-500/40 bg-yellow-500/10 text-yellow-300",
  failed: "border-red-500/40 bg-red-500/10 text-red-300",
  created: "border-white/20 bg-white/5 text-white/60",
};

const STATUS_LABEL = {
  paid: "Paid",
  cod_pending: "COD pending",
  failed: "Failed",
  created: "Payment pending",
};

const FULFIL_STYLE = {
  confirmed: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  dispatched: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  delivered: "border-[#7ED321]/40 bg-[#7ED321]/10 text-[#7ED321]",
  cancelled: "border-red-500/40 bg-red-500/10 text-red-300",
};

const FULFIL_LABEL = {
  confirmed: "Confirmed",
  dispatched: "Dispatched",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const FULFIL_ACTIONS = {
  confirmed: [["dispatched", "Dispatch"], ["cancelled", "Cancel"]],
  dispatched: [["delivered", "Mark Delivered"], ["cancelled", "Cancel"]],
  delivered: [],
  cancelled: [["confirmed", "Reopen"]],
};

const customerWhatsApp = (o, message) => {
  let d = (o.customer.phone || "").replace(/\D/g, "");
  if (d.length === 10) d = `91${d}`;
  window.open(`https://wa.me/${d}?text=${encodeURIComponent(message)}`, "_blank");
};

const dispatchMessage = (o) =>
  `Hi ${(o.customer.name || "").split(" ")[0]}! Great news — your Zentra order ${o.order_number} has been dispatched and is on its way.\n\nTrack the live status anytime here: ${window.location.origin}/track\n\nEveryday Protein. Powered by Microgreens. — Team Zentra`;

const OrdersList = () => {
  const [orders, setOrders] = useState(null);
  const [denied, setDenied] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [noteOpen, setNoteOpen] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const toggleNote = (o) => {
    if (noteOpen === o.id) return setNoteOpen(null);
    setNoteOpen(o.id);
    setNoteText(o.note || "");
  };

  const saveNote = async (o) => {
    setSavingNote(true);
    try {
      await axios.put(`${API}/admin/orders/${o.id}/note`, { note: noteText.trim() }, { headers: adminHeaders() });
      setOrders((list) => list.map((x) => (x.id === o.id ? { ...x, note: noteText.trim() } : x)));
      toast.success("Note saved.");
      setNoteOpen(null);
    } catch {
      toast.error("Could not save the note.");
    } finally {
      setSavingNote(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/admin/orders`, { headers: adminHeaders() })
      .then((r) => setOrders(r.data))
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("zentra-admin-token");
          setDenied(true);
        }
      });
  }, []);

  const setFulfilment = async (o, status) => {
    setUpdating(o.id);
    try {
      await axios.put(`${API}/admin/orders/${o.id}/status`, { status }, { headers: adminHeaders() });
      setOrders((list) => list.map((x) => (x.id === o.id ? { ...x, fulfilment_status: status } : x)));
      toast.success(`Order marked ${FULFIL_LABEL[status]}.`, {
        action:
          status === "dispatched"
            ? { label: "WhatsApp Customer", onClick: () => customerWhatsApp(o, dispatchMessage(o)) }
            : undefined,
      });
    } catch {
      toast.error("Could not update the order status.");
    } finally {
      setUpdating(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/orders/export`, {
        headers: adminHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const dispo = res.headers["content-disposition"] || "";
      const match = dispo.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "zentra-orders.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Orders CSV downloaded.");
    } catch {
      toast.error("Export failed — please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (denied) return <p className="text-sm text-red-400">Session expired — please log in again from /admin.</p>;
  if (!orders) return <p className="text-sm text-white/40">Loading orders…</p>;

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/40" data-testid="orders-count">
          {orders.length} {orders.length === 1 ? "order" : "orders"}
        </p>
        <button
          onClick={exportCsv}
          disabled={exporting || orders.length === 0}
          data-testid="orders-export-button"
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white/70 transition-colors hover:border-[#7ED321] hover:text-[#7ED321] disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" />
          {exporting ? "Preparing…" : "Export CSV"}
        </button>
      </div>

      {orders.length === 0 ? (
        <p className="text-sm text-white/40" data-testid="orders-empty">No orders yet. New orders will appear here.</p>
      ) : (
        <div className="overflow-x-auto" data-testid="orders-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-[11px] uppercase tracking-[0.15em] text-white/40">
                <th className="py-3 pr-4">Order</th>
                <th className="py-3 pr-4">Date</th>
                <th className="py-3 pr-4">Customer</th>
                <th className="py-3 pr-4">Items</th>
                <th className="py-3 pr-4">Total</th>
                <th className="py-3 pr-4">Method</th>
                <th className="py-3 pr-4">Payment</th>
                <th className="py-3">Fulfilment</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <Fragment key={o.id}>
                <tr className="border-b border-white/5" data-testid={`order-row-${o.order_number}`}>
                  <td className="py-3.5 pr-4 font-semibold text-white">
                    {o.order_number}
                    <button
                      onClick={() => toggleNote(o)}
                      data-testid={`order-note-toggle-${o.order_number}`}
                      className="mt-1 block text-[11px] font-bold uppercase tracking-wide text-white/40 underline underline-offset-2 transition-colors hover:text-[#7ED321]"
                    >
                      {o.note ? "Edit note" : "+ Note"}
                    </button>
                    {o.note && noteOpen !== o.id && (
                      <span className="mt-1 block max-w-[150px] truncate text-[11px] font-normal normal-case tracking-normal text-amber-300/80" data-testid={`order-note-preview-${o.order_number}`}>
                        {o.note}
                      </span>
                    )}
                  </td>
                  <td className="py-3.5 pr-4 text-white/50">{new Date(o.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="py-3.5 pr-4">
                    <span className="block text-white">{o.customer.name}</span>
                    <span className="text-xs text-white/40">{o.customer.phone} · {o.customer.pincode}</span>
                  </td>
                  <td className="py-3.5 pr-4 text-white/60">
                    {o.items.map((i) => `${i.qty}× ${i.sku}`).join(", ")}
                  </td>
                  <td className="py-3.5 pr-4 font-semibold text-white">{inr(o.total)}</td>
                  <td className="py-3.5 pr-4 uppercase text-white/50">{{ cod: "COD", payu: "PayU", razorpay: "Razorpay" }[o.payment_method] || o.payment_method}</td>
                  <td className="py-3.5 pr-4">
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${STATUS_STYLE[o.status] || STATUS_STYLE.created}`} data-testid={`order-status-${o.order_number}`}>
                      {STATUS_LABEL[o.status] || o.status}
                    </span>
                  </td>
                  <td className="py-3.5">
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${FULFIL_STYLE[o.fulfilment_status] || FULFIL_STYLE.confirmed}`} data-testid={`order-fulfilment-${o.order_number}`}>
                      {FULFIL_LABEL[o.fulfilment_status] || "Confirmed"}
                    </span>
                    <div className="mt-2 flex items-center gap-3">
                      {(FULFIL_ACTIONS[o.fulfilment_status] || FULFIL_ACTIONS.confirmed).map(([st, label]) => (
                        <button
                          key={st}
                          onClick={() => setFulfilment(o, st)}
                          disabled={updating === o.id}
                          data-testid={`order-${st}-${o.order_number}`}
                          className="text-[11px] font-bold uppercase tracking-wide text-white/50 underline underline-offset-2 transition-colors hover:text-[#7ED321] disabled:opacity-40"
                        >
                          {label}
                        </button>
                      ))}
                      {o.fulfilment_status === "dispatched" && (
                        <button
                          onClick={() => customerWhatsApp(o, dispatchMessage(o))}
                          data-testid={`order-whatsapp-${o.order_number}`}
                          title="Send dispatch message to customer on WhatsApp"
                          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#25D366] underline underline-offset-2 transition-colors hover:text-[#2be374]"
                        >
                          <MessageCircle className="h-3 w-3" />
                          WhatsApp
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {noteOpen === o.id && (
                  <tr className="border-b border-white/5 bg-white/[0.02]" data-testid={`order-note-editor-${o.order_number}`}>
                    <td colSpan={8} className="px-1 py-4">
                      <label className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white/40">
                        Order note — courier docket, instructions (internal only, never shown to customers)
                      </label>
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        rows={2}
                        placeholder="e.g. BlueDart AWB 78213456 · deliver after 5pm · call before delivery"
                        data-testid={`order-note-input-${o.order_number}`}
                        className="w-full rounded-xl border border-white/15 bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#7ED321]"
                      />
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => saveNote(o)}
                          disabled={savingNote}
                          data-testid={`order-note-save-${o.order_number}`}
                          className="rounded-full bg-[#7ED321] px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331] disabled:opacity-50"
                        >
                          {savingNote ? "Saving…" : "Save Note"}
                        </button>
                        <button
                          onClick={() => setNoteOpen(null)}
                          data-testid={`order-note-close-${o.order_number}`}
                          className="rounded-full border border-white/20 px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white/60 transition-colors hover:border-white/40"
                        >
                          Close
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const AdminOrders = () => (
  <RequireAdmin>
    <AdminShell title="Orders">
      <OrdersList />
    </AdminShell>
  </RequireAdmin>
);

export default AdminOrders;
